import { Link, useParams } from 'react-router-dom';
import { useSession } from '../App';
import { api, useQuery } from '../api/client';
import { BandPill, Card, Empty, ErrorNote, Score, Skeleton } from '../components/ui';
import { formatDate } from '../lib/format';

/**
 * Coaching points.
 *
 * A rep's coaching bank: every call a manager flagged from the call's
 * Highlights tab, with the comment they wrote. Managers and admins pick an
 * SDR first; an SDR who opens this page goes straight to their own profile -
 * the same personalization the Calls and Dashboard pages already use.
 */
export function CoachingPointsPage() {
  const { sdrId: routeSdrId } = useParams();
  const { user } = useSession();
  const isSdr = user?.role === 'sdr';
  const effectiveSdrId = routeSdrId ?? (isSdr ? (user?.sdrId ?? null) : null);

  if (!effectiveSdrId) return <SdrPicker />;
  return <SdrCoachingProfile sdrId={effectiveSdrId} showBack={!isSdr} />;
}

function SdrPicker() {
  const { data, error, loading, reload } = useQuery(() => api.sdrs(), []);

  return (
    <>
      <header className="page-head">
        <div className="page-head-text">
          <h1>Coaching points</h1>
          <p className="page-sub">Pick a rep to see the calls flagged for coaching and the notes left on them.</p>
        </div>
      </header>
      <div className="page-body page-enter">
        <ErrorNote error={error} onRetry={reload} />
        {loading && !data ? (
          <Card>
            <div className="stack" style={{ gap: 9 }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} height={48} />
              ))}
            </div>
          </Card>
        ) : !data?.sdrs.length ? (
          <Card>
            <Empty title="No SDRs yet" body="Add an SDR before coaching points can be tracked." />
          </Card>
        ) : (
          <div className="stack" style={{ gap: 10 }}>
            {data.sdrs.map((s) => (
              <Link key={s.id} to={`/coaching/${s.id}`} className="card card-link row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div className="cell-primary" style={{ fontSize: 15 }}>
                    {s.name}
                  </div>
                  <div className="cell-secondary">{s.team ?? 'No team recorded'}</div>
                </div>
                {s.coachingHighlightCount > 0 ? (
                  <span className="pill pill-sm pill-warn">
                    {s.coachingHighlightCount} flagged
                  </span>
                ) : (
                  <span className="small dim">No flagged calls</span>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function SdrCoachingProfile({ sdrId, showBack }: { sdrId: string; showBack: boolean }) {
  const { data, error, loading, reload } = useQuery(() => api.sdrCoachingHighlights(sdrId), [sdrId]);

  return (
    <>
      <header className="page-head">
        <div className="page-head-text">
          <h1>{data?.sdr.name ?? 'Coaching points'}</h1>
          <p className="page-sub">
            {data ? `${data.highlights.length} call${data.highlights.length === 1 ? '' : 's'} flagged for coaching` : 'Loading…'}
          </p>
        </div>
        {showBack && (
          <div className="page-head-actions">
            <Link to="/coaching" className="btn btn-ghost btn-sm">
              ← All reps
            </Link>
          </div>
        )}
      </header>
      <div className="page-body page-enter">
        <ErrorNote error={error} onRetry={reload} />
        {loading && !data ? (
          <Card>
            <div className="stack" style={{ gap: 9 }}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} height={90} />
              ))}
            </div>
          </Card>
        ) : !data?.highlights.length ? (
          <Card>
            <Empty
              title="Nothing flagged yet"
              body="When a call is added to this rep's coaching profile from its Highlights tab, it will show up here with the note that was written."
            />
          </Card>
        ) : (
          <div className="stack" style={{ gap: 12 }}>
            {data.highlights.map((h) => (
              <Link key={h.callId} to={`/calls/${h.callId}`} className="card card-link">
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div>
                    <div className="cell-primary">{h.prospectName ?? 'Unnamed prospect'}</div>
                    <div className="cell-secondary">{h.prospectCompany ?? 'No company recorded'}</div>
                  </div>
                  <div className="row" style={{ gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <Score value={h.overallScore} showMax={false} />
                    <BandPill band={h.band} />
                  </div>
                </div>
                <div className="mt-2 small muted">{formatDate(h.calledAt)}</div>
                {h.comment && <p className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>{h.comment}</p>}
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
